import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ─── CANONICAL ROLE NAMES ─────────────────────────────────────────────────────
// Use these constants everywhere. Never store bare 'Executor' as a role value.
export const ROLE_EXECUTOR    = 'Named as executor';
export const ROLE_MEDICAL_POA = 'Medical POA';
export const ROLE_POA         = 'Power of Attorney';

// ─── CANONICAL ROLE PERMISSIONS ───────────────────────────────────────────────
// Single source of truth for client-side role permission defaults.
// IMPORTANT: supabase/functions/_shared/rolePermissions.ts is the Edge Function
// counterpart. The two cannot share a module (Deno vs. browser bundler), so they
// are kept in sync manually. If you change values here, update that file too.
// App.jsx's DEFAULT_EXECUTOR_PERMISSIONS / DEFAULT_MPOA_PERMISSIONS /
// DEFAULT_POA_PERMISSIONS alias these objects — do not define separate copies.
export const ROLE_PERMISSIONS = {
  [ROLE_EXECUTOR]: {
    view_documents:             true,
    download_documents:         true,
    view_checklist:             true,
    edit_checklist:             true,
    view_vault:                 true,
    view_family:                true,
    full_access:                false,
    view_medical_documents:     false,
    download_medical_documents: false,
  },
  [ROLE_MEDICAL_POA]: {
    view_medical_documents:     true,
    download_medical_documents: true,
    view_checklist:             false,
    view_documents:             false,
    download_documents:         false,
    view_vault:                 false,
    view_family:                false,
    full_access:                false,
    edit_checklist:             false,
  },
  [ROLE_POA]: {
    view_documents:             true,
    download_documents:         true,
    view_checklist:             true,
    view_vault:                 true,
    view_family:                true,
    full_access:                false,
    edit_checklist:             false,
    view_medical_documents:     false,
    download_medical_documents: false,
  },
};

// ─── AUTH HELPERS ─────────────────────────────────────────────────────────────

export const signUp = async (email, password) => {
  const { data, error } = await supabase.auth.signUp({ email, password });
  return { data, error };
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const updateEmail = async (newEmail) => {
  const { data, error } = await supabase.auth.updateUser({ email: newEmail });
  return { data, error };
};

export const updatePassword = async (newPassword) => {
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  return { data, error };
};

export const sendPasswordReset = async (email) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/?type=recovery`,
  });
  return { data, error };
};

export const deleteAccount = async () => {
  const { error } = await supabase.rpc('delete_user');
  return { error };
};

// ─── ESTATE HELPERS ───────────────────────────────────────────────────────────

export const saveEstate = async (userId, estateData) => {
  // Check if estate already exists for this user. Use order+limit before maybeSingle
  // so that duplicate rows (from race-condition double-inserts) never cause it to
  // return null and trigger yet another INSERT — always pick the most-recent row.
  const { data: existing } = await supabase
    .from('estates')
    .select('id')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  const payload = {
    user_id: userId,
    owner_user_id: userId,
    signup_mode: estateData.signupMode,
    owner_name: estateData.ownerName,
    owner_email: estateData.ownerEmail,
    owner_phone: estateData.ownerPhone,
    owner_state: estateData.state || estateData.ownerState,
    deceased_name: estateData.deceasedName,
    deceased_state: estateData.deceasedState,
    date_of_death: estateData.dateOfDeath,
    has_will: estateData.hasWill,
    has_property: estateData.hasProperty,
    estate_value: estateData.estateValue,
    executor_name: estateData.executorName,
    executor_email: estateData.executorEmail,
    executor_phone: estateData.executorPhone,
    executor_relation: estateData.executorRelation,
    executor_address: estateData.executorAddress,
    invite_executor: estateData.inviteExecutor,
    invite_executor_email: estateData.inviteExecutorEmail,
    backup1_name: estateData.backup1Name || estateData.inviteBackup1Name,
    backup1_email: estateData.backup1Email || estateData.inviteBackup1Email,
    backup2_name: estateData.backup2Name || estateData.inviteBackup2Name,
    backup2_email: estateData.backup2Email || estateData.inviteBackup2Email,
  };

  if (existing) {
    const { data, error } = await supabase
      .from('estates')
      .update(payload)
      .eq('user_id', userId)
      .select()
      .maybeSingle();
    return { data, error };
  } else {
    const { data, error } = await supabase
      .from('estates')
      .insert(payload)
      .select()
      .maybeSingle();
    return { data, error };
  }
};

export const loadEstate = async (userId) => {
  const { data, error } = await supabase
    .from('estates')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  return { data, error };
};

// ─── TASKS HELPERS ────────────────────────────────────────────────────────────

export const saveTasks = async (userId, estateId, tasks) => {
  // Delete existing tasks and reinsert
  await supabase.from('tasks').delete().eq('user_id', userId);

  if (!tasks.length) return { data: [], error: null };

  const payload = tasks.map(t => ({
    user_id: userId,
    estate_id: estateId,
    task_id: t.id,
    title: t.title,
    category: t.category,
    done: t.done,
    urgency: t.urgency,
    description: t.description,
  }));

  const { data, error } = await supabase.from('tasks').insert(payload).select();
  return { data, error };
};

export const loadTasks = async (userId) => {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });
  return { data, error };
};

export const updateTask = async (userId, taskId, done) => {
  const { data, error } = await supabase
    .from('tasks')
    .update({ done })
    .eq('user_id', userId)
    .eq('task_id', taskId);
  return { data, error };
};

// ─── COLLABORATORS HELPERS ────────────────────────────────────────────────────

export const saveCollaborator = async (userId, estateId, collaborator) => {
  const { data, error } = await supabase
    .from('collaborators')
    .insert({
      user_id: userId,
      estate_id: estateId,
      name: collaborator.name,
      email: collaborator.email,
      role: collaborator.role,
      relationship: collaborator.relationship,
      can_collaborate: collaborator.canCollaborate,
      invite_status: collaborator.inviteStatus,
      joined: collaborator.joined,
    })
    .select()
    .maybeSingle();
  return { data, error };
};

export const loadCollaborators = async (userId) => {
  const { data, error } = await supabase
    .from('collaborators')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });
  return { data, error };
};

export const updateCollaborator = async (userId, collaboratorId, updates) => {
  const { data, error } = await supabase
    .from('collaborators')
    .update({
      name: updates.name,
      email: updates.email,
      role: updates.role,
      relationship: updates.relationship,
      can_collaborate: updates.canCollaborate,
      invite_status: updates.inviteStatus,
    })
    .eq('id', collaboratorId)
    .eq('user_id', userId);
  return { data, error };
};

// ─── VAULT HELPERS ────────────────────────────────────────────────────────────

export const saveVaultItem = async (userId, estateId, item) => {
  const { data, error } = await supabase
    .from('vault_items')
    .insert({
      user_id: userId,
      estate_id: estateId,
      title: item.title,
      category: item.category,
      subcategory: item.subcategory || null,
      notes: item.notes,
      access_level: item.accessLevel,
      details: item.details || {},
      shared_with_medical_poa: item.shared_with_medical_poa === true,
      joint_estate_id: item.joint_estate_id || null,
    })
    .select()
    .maybeSingle();
  return { data, error };
};

export const loadVaultItems = async (userId) => {
  const { data, error } = await supabase
    .from('vault_items')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });
  return { data, error };
};

export const updateVaultItem = async (userId, itemId, updates) => {
  const { data, error } = await supabase
    .from('vault_items')
    .update({
      title: updates.title,
      notes: updates.notes,
      details: updates.details,
      shared_with_medical_poa: updates.shared_with_medical_poa === true,
    })
    .eq('id', itemId)
    .eq('user_id', userId);
  return { data, error };
};

export const deleteVaultItem = async (userId, itemId) => {
  const { data, error } = await supabase
    .from('vault_items')
    .delete()
    .eq('id', itemId)
    .eq('user_id', userId);
  return { data, error };
};

// ─── PROFILE HELPERS ──────────────────────────────────────────────────────────

export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  return { data, error };
};

export const updateProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('profiles')
    .upsert({ id: userId, ...updates }, { onConflict: 'id' })
    .select()
    .maybeSingle();
  return { data, error };
};

export const markOnboardingComplete = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ onboarding_complete: true })
    .eq('id', userId)
    .select()
    .maybeSingle();
  return { data, error };
};

// ─── EMAIL HELPER ──────────────────────────────────────────────────────────────
// TODO: This calls Resend directly from the frontend, which exposes the API key
// in the browser bundle. Acceptable for testing. Move to a server-side function
// (Supabase Edge Function or Next.js API route) before going to production.

const _getEmailContent = (type, d = {}) => {
  const base = (headerHtml, bodyHtml) => `<!DOCTYPE html><html><head><style>
    body{font-family:Georgia,serif;background:#f8f5f0;margin:0;padding:40px 20px}
    .container{max-width:560px;margin:0 auto;background:white;border-radius:16px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.08)}
    .header{background:linear-gradient(135deg,#2c4a3e,#3d6b5a);padding:40px;text-align:center;color:white}
    .header h1{margin:0 0 8px;font-size:26px;font-family:Georgia,serif}
    .header p{margin:0;opacity:0.8;font-size:14px}
    .body{padding:40px;color:#4a5568;line-height:1.7;font-size:15px}
    .btn{display:block;background:#2c4a3e;color:white!important;text-decoration:none;padding:16px 32px;border-radius:10px;text-align:center;font-size:16px;font-weight:600;margin:24px 0}
    .btn-red{background:#c0392b}
    .footer{padding:24px 40px;border-top:1px solid #ece7de;text-align:center;font-size:12px;color:#7a8694}
  </style></head><body><div class="container">
    ${headerHtml}
    ${bodyHtml}
    <div class="footer"><p>HeirOS — Estate planning for the living. Settlement support for the grieving.</p><p style="color:#b8935a">HeirOS is not a law firm. This is not legal advice.</p></div>
  </div></body></html>`;

  switch (type) {
    case 'invitation':
      return {
        subject: `${d.inviterName || 'Someone'} has invited you to HeirOS`,
        html: `<!DOCTYPE html><html><head><style>
  body{font-family:Georgia,serif;background:#f8f5f0;margin:0;padding:40px 20px}
  .container{max-width:560px;margin:0 auto;background:white;border-radius:16px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.08)}
  .header{background:linear-gradient(135deg,#2c4a3e,#3d6b5a);padding:40px;text-align:center;color:white}
  .header h1{margin:0 0 8px;font-size:26px;font-family:Georgia,serif}
  .header p{margin:0;opacity:0.8;font-size:14px}
  .body{padding:40px;color:#4a5568;line-height:1.7;font-size:15px}
  .code-box{background:#f0ebe3;border:2px solid #b8935a;border-radius:12px;padding:24px;text-align:center;margin:24px 0}
  .code-box p{margin:0 0 8px;font-size:13px;color:#7a8694;text-transform:uppercase;letter-spacing:0.08em}
  .code-box .code{font-size:36px;font-weight:700;color:#2c4a3e;letter-spacing:0.1em;font-family:monospace}
  .steps{background:#f8f5f0;border-radius:10px;padding:20px 20px 20px 36px;margin:20px 0}
  .steps li{margin-bottom:8px;font-size:14px;color:#4a5568}
  .free-badge{display:inline-block;background:#f0faf5;border:1px solid #c3e6cb;border-radius:8px;padding:8px 16px;font-size:13px;color:#2c7a5e;font-weight:600;margin-bottom:16px}
  .footer{padding:24px 40px;border-top:1px solid #ece7de;text-align:center;font-size:12px;color:#7a8694}
  .disclaimer{color:#b8935a;margin-top:8px}
</style></head><body><div class="container">
  <div class="header"><h1>You've been invited</h1><p>to collaborate on HeirOS</p></div>
  <div class="body">
    <p>Hi ${d.inviteeName || 'there'},</p>
    <p><strong>${d.inviterName || 'Someone'}</strong> has invited you to join their estate plan on HeirOS as <strong>${d.roleName || 'Collaborator'}</strong>.</p>
    <div class="free-badge">✓ No payment required — you have been invited as a guest</div>
    <p>Use this code to accept your invitation:</p>
    <div class="code-box">
      <p>Your invite code</p>
      <div class="code">${d.inviteCode || 'HEIR-XXXXXX'}</div>
    </div>
    <a href="https://heiros.estate?code=${d.inviteCode || ''}&email=${encodeURIComponent(d.inviteeEmail || '')}" style="display:block;background:#2c4a3e;color:white!important;text-decoration:none;padding:16px 32px;border-radius:10px;text-align:center;font-size:16px;font-weight:600;margin:24px 0">Accept My Invitation →</a>
    <p style="font-size:13px;color:#7a8694;text-align:center">Or go to <strong>heiros.estate</strong>, click <strong>Get Started → I was invited</strong>, and enter your code manually: <strong>${d.inviteCode || 'HEIR-XXXXXX'}</strong></p>
    <p style="font-size:13px;color:#7a8694">This invitation expires in 30 days. If you did not expect this email you can safely ignore it.</p>
  </div>
  <div class="footer">
    <p>HeirOS — Estate planning for the living. Settlement support for the grieving.</p>
    <p class="disclaimer">HeirOS is not a law firm. This is not legal advice.</p>
  </div>
</div></body></html>`,
      };
    case 'death_reported':
      return {
        subject: '⚠ Important: Your passing has been reported on HeirOS',
        html: base(
          `<div class="header" style="background:linear-gradient(135deg,#8a3a2a,#c0392b)"><h1>⚠ Important Notice</h1><p>Action required</p></div>`,
          `<div class="body"><p>Hi ${d.ownerName || 'there'},</p>
          <p>Your executor <strong>${d.executorName || 'your executor'}</strong> has reported your passing on HeirOS.</p>
          <p><strong>If you are alive, click below immediately to cancel this report.</strong></p>
          <a href="${d.cancelUrl || 'https://heiros.estate'}" class="btn btn-red">Cancel This Report →</a>
          <p>If you do not cancel within 72 hours, your executor will be granted full access to your estate and vault.</p>
          <p style="font-size:13px;color:#7a8694">Deadline: ${d.deadline || '72 hours from now'}</p></div>`
        ),
      };
    case 'death_confirmed':
      return {
        subject: `HeirOS Estate Access — ${d.deceasedName || 'Estate'} is now accessible`,
        html: base(
          `<div class="header"><h1>Estate Access Active</h1><p>We are sorry for your loss</p></div>`,
          `<div class="body"><p>We are deeply sorry for the passing of <strong>${d.deceasedName || 'your family member'}</strong>.</p>
          <p>Access to their HeirOS estate is now active. Everything they organized is here for you.</p>
          <a href="https://heiros.estate" class="btn">Begin Settlement →</a></div>`
        ),
      };
    case 'death_cancelled':
      return {
        subject: 'Death report cancelled — HeirOS',
        html: base(
          `<div class="header"><h1>Death Report Cancelled</h1><p>No further action needed</p></div>`,
          `<div class="body"><p>${d.ownerName || 'The estate owner'} has cancelled the death report on HeirOS.</p>
          <p>Estate access has been reverted to planning mode. No executor access was granted.</p>
          <p>If you believe this was done in error, please contact ${d.ownerName || 'the estate owner'} directly.</p></div>`
        ),
      };
    case 'annual_review':
      return {
        subject: "It's time to review your HeirOS estate plan",
        html: base(
          `<div class="header"><h1>Annual Estate Review</h1><p>Time for a quick check-in</p></div>`,
          `<div class="body"><p>Hi ${d.ownerName || 'there'},</p>
          <p>It's been a year since you last reviewed your HeirOS estate plan. Life changes — and your estate plan should keep up.</p>
          <a href="https://heiros.estate" class="btn">Review My Estate →</a>
          <p style="font-size:13px;color:#7a8694">Updating takes about 5 minutes. Your executor will thank you.</p></div>`
        ),
      };
    case 'access_revoked':
      return {
        subject: 'Your HeirOS estate access has been changed',
        html: base(
          `<div class="header"><h1>Estate Access Updated</h1><p>A change was made to your HeirOS access</p></div>`,
          `<div class="body"><p>Hi ${d.name || 'there'},</p>
          <p><strong>${d.ownerName || 'The estate owner'}</strong> has updated your access on HeirOS.</p>
          <p>Your role as <strong>${(d.role || 'collaborator').replace(/_/g, ' ')}</strong> has been removed from this estate.</p>
          <p>If you believe this was done in error, please contact <strong>${d.ownerName || 'the estate owner'}</strong> directly.</p></div>`
        ),
      };
    case 'access_request_to_backup':
      return {
        subject: `${d.requesterName || 'Your executor'} has requested emergency estate access`,
        html: base(
          `<div class="header"><h1>Emergency Access Request</h1><p>Your review is needed</p></div>`,
          `<div class="body">
          <p>Hi there,</p>
          <p><strong>${d.requesterName || 'An executor'}</strong> (Backup Executor #2) has requested full access to <strong>${d.ownerName || "the estate owner"}'s HeirOS estate.</strong></p>
          <p>As Backup Executor #1, you can deny this request within <strong>${d.responseWindow || 72} hours</strong>.</p>
          <div style="text-align:center;margin:2rem 0;">
            <a href="${d.denyUrl}" style="background:#c0392b;color:white;padding:0.875rem 2rem;border-radius:8px;text-decoration:none;font-weight:600;display:inline-block;">Deny This Request →</a>
          </div>
          <p style="font-size:0.85em;color:#666;">If you do not respond, the request will be forwarded to the primary executor automatically after ${d.responseWindow || 72} hours.</p>
          </div>`
        ),
      };
    case 'access_request_to_primary':
      return {
        subject: `${d.requesterName || 'An executor'} has requested emergency estate access`,
        html: base(
          `<div class="header"><h1>Emergency Access Request</h1><p>Your review is needed</p></div>`,
          `<div class="body">
          <p>Hi there,</p>
          <p><strong>${d.requesterName || 'An executor'}</strong> has requested full access to <strong>${d.ownerName || "the estate owner"}'s HeirOS estate.</strong></p>
          <p>As the Primary Executor, you can deny this request within <strong>${d.responseWindow || 72} hours</strong>.</p>
          <div style="text-align:center;margin:2rem 0;">
            <a href="${d.denyUrl}" style="background:#c0392b;color:white;padding:0.875rem 2rem;border-radius:8px;text-decoration:none;font-weight:600;display:inline-block;">Deny This Request →</a>
          </div>
          <p style="font-size:0.85em;color:#666;">If you do not respond, the request will be forwarded to the estate owner automatically.</p>
          </div>`
        ),
      };
    case 'access_request_to_owner':
      return {
        subject: `\u26A0 Your executor has requested full access to your estate`,
        html: base(
          `<div class="header"><h1>Estate Access Request</h1><p>Action may be required</p></div>`,
          `<div class="body">
          <p>Hi ${d.ownerName || 'there'},</p>
          <p><strong>${d.executorName || 'Your executor'}</strong> has requested full access to your HeirOS estate.</p>
          <p>If you are alive and do not want to grant this access, click the button below within <strong>${d.responseWindow || 72} hours</strong>.</p>
          <div style="text-align:center;margin:2rem 0;">
            <a href="${d.denyUrl}" style="background:#c0392b;color:white;padding:1rem 2.5rem;border-radius:8px;text-decoration:none;font-weight:700;display:inline-block;font-size:1rem;">Deny This Request →</a>
          </div>
          <p>If you do not respond by <strong>${d.deadline || 'the deadline'}</strong>, <strong>${d.executorName || 'your executor'}</strong> will automatically receive full access to your estate documents, vault, and checklist.</p>
          <p style="font-size:0.85em;color:#666;">This may mean your executor believes you are unreachable or have passed away. If this was requested by mistake, your executor can cancel it from their HeirOS account.</p>
          </div>`
        ),
      };
    case 'access_granted':
      return {
        subject: 'Full estate access has been granted — HeirOS',
        html: base(
          `<div class="header"><h1>Access Granted</h1><p>Your emergency access request was approved</p></div>`,
          `<div class="body">
          <p>Hi ${d.executorName || 'there'},</p>
          <p>Your request for full access to <strong>${d.ownerName || "the estate owner"}'s estate</strong> has been granted.</p>
          <p>${d.ownerName || 'The estate owner'} did not respond within the required window. You now have full access to all estate information.</p>
          <div style="text-align:center;margin:2rem 0;">
            <a href="https://heiros.estate" style="background:#2c4a3e;color:white;padding:0.875rem 2rem;border-radius:8px;text-decoration:none;font-weight:600;display:inline-block;">View Estate →</a>
          </div>
          </div>`
        ),
      };
    case 'access_denied':
      return {
        subject: 'Estate access request denied — HeirOS',
        html: base(
          `<div class="header"><h1>Access Request Denied</h1><p>Your request was not approved</p></div>`,
          `<div class="body">
          <p>Hi ${d.executorName || 'there'},</p>
          <p>Your request for full access to <strong>${d.ownerName || "the estate owner"}'s estate</strong> was denied.</p>
          ${d.denialReason ? `<p><strong>Reason:</strong> <em>${d.denialReason}</em></p>` : ''}
          <p>If you believe this was done in error, please contact <strong>${d.ownerName || 'the estate owner'}</strong> directly.</p>
          </div>`
        ),
      };
    case 'access_cancelled':
      return {
        subject: 'Estate access request cancelled — HeirOS',
        html: base(
          `<div class="header"><h1>Access Request Cancelled</h1><p>No action needed</p></div>`,
          `<div class="body">
          <p>Hi ${d.ownerName || 'there'},</p>
          <p><strong>${d.executorName || 'Your executor'}</strong> has cancelled their request for full access to your estate.</p>
          <p>No action is needed. Your estate data remains private.</p>
          </div>`
        ),
      };
    default:
      return { subject: '', html: '' };
  }
};

export const sendEmail = async (to, type, data = {}) => {
  console.log('6. [HeirOS] sendEmail → invoking edge function:', { to, type });
  try {
    const { data: result, error } = await supabase.functions.invoke('send-email', {
      body: { to, type, data },
    });
    console.log('6b. [HeirOS] sendEmail ← edge function response:', { result, error });
    if (error) console.error('[HeirOS] sendEmail ← edge function error:', error);
    return { result, error: error || null };
  } catch (e) {
    console.error('[HeirOS] sendEmail ← exception:', e);
    return { result: null, error: e };
  }
};

// ─── COLLABORATOR / INVITATION REMOVAL ────────────────────────────────────────

export const removeCollaboratorById = async (collaboratorId) => {
  const { data, error } = await supabase
    .from('collaborators')
    .delete()
    .eq('id', collaboratorId)
    .select()
    .maybeSingle();
  return { data, error };
};

export const removeInvitationByEmail = async (estateId, email) => {
  const { error } = await supabase
    .from('invitations')
    .delete()
    .eq('estate_id', estateId)
    .ilike('invited_email', email);
  return { error };
};

export const leaveEstate = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ invited_estate_id: null, invited_role: null })
    .eq('id', userId)
    .select()
    .maybeSingle();
  await supabase
    .from('invitations')
    .update({ status: 'declined' })
    .eq('accepted_by', userId)
    .in('status', ['pending', 'accepted']);
  return { data, error };
};

// ─── ESTATE EXECUTOR UPDATE ────────────────────────────────────────────────────

export const updateEstateExecutor = async (userId, rank, name, email) => {
  const fieldMap = {
    1: { name_field: 'invite_executor',  email_field: 'invite_executor_email' },
    2: { name_field: 'backup1_name',     email_field: 'backup1_email' },
    3: { name_field: 'backup2_name',     email_field: 'backup2_email' },
  };
  const f = fieldMap[rank];
  if (!f) return { error: new Error('Invalid executor rank') };
  const { data, error } = await supabase
    .from('estates')
    .update({ [f.name_field]: name, [f.email_field]: email })
    .eq('user_id', userId)
    .select()
    .maybeSingle();
  return { data, error };
};

// ─── INVITATION HELPERS ────────────────────────────────────────────────────────

const generateInviteCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'HEIR-';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// emailContext: { inviterName: string }
export const createInvitation = async (userId, estateId, inviteData, emailContext) => {
  if (!estateId) return { data: null, error: new Error('estateId is required for invitation') };
  if (!userId) return { data: null, error: new Error('userId is required for invitation') };

  const invite_code = generateInviteCode();

  const payload = {
    estate_id: estateId,
    invited_by: userId,
    invited_email: inviteData.email.toLowerCase().trim(),
    invited_name: inviteData.name,
    role: inviteData.role,
    permissions: inviteData.permissions || {},
    invite_code,
    ...(emailContext?.inviterName ? { inviter_name: emailContext.inviterName } : {}),
    ...(inviteData.executor_order != null ? { executor_order: inviteData.executor_order } : {}),
    ...(inviteData.medical_poa_order != null ? { medical_poa_order: inviteData.medical_poa_order } : {}),
    ...(inviteData.poa_order != null ? { poa_order: inviteData.poa_order } : {}),
    ...(inviteData.is_couple_invite ? { is_couple_invite: true } : {}),
    ...(inviteData.is_couple_bundle ? { is_couple_bundle: true } : {}),
    ...(inviteData.couple_roles ? { couple_roles: inviteData.couple_roles } : {}),
  };

  const { data, error } = await supabase
    .from('invitations')
    .insert(payload)
    .select()
    .maybeSingle();

  if (!error && data && inviteData.email) {
    sendEmail(inviteData.email, 'invitation', {
      inviteeName: inviteData.name || '',
      inviterName: emailContext?.inviterName || '',
      roleName: inviteData.role || 'Collaborator',
      inviteCode: invite_code,
      inviteeEmail: inviteData.email,
      acceptUrl: `https://heiros.estate?code=${invite_code}&email=${encodeURIComponent(inviteData.email)}`,
    });
  }

  return { data, error, invite_code };
};

// Routed through a SECURITY DEFINER function instead of a direct table read.
// This lookup runs pre-auth, which previously forced a permissive
// `USING (true)` SELECT policy on invitations -- that policy exposed every
// user's email and family structure to anyone holding the public anon key.
// The function returns the same shape (including the nested `estates` object)
// so callers in App.jsx are unchanged. It never returns the invite token.
export const findInvitationByCode = async (email, code) => {
  const { data, error } = await supabase.rpc('lookup_invitation_by_code', {
    p_email: email.toLowerCase().trim(),
    p_code:  code.toUpperCase().trim(),
  });
  return { data: data ?? null, error };
};

export const acceptInvitation = async (invitationId, userId) => {
  const { data, error } = await supabase
    .from('invitations')
    .update({ status: 'accepted', accepted_by: userId, accepted_at: new Date().toISOString() })
    .eq('id', invitationId)
    .select()
    .maybeSingle();
  return { data, error };
};

export const getInvitationsForEstate = async (estateId) => {
  const { data, error } = await supabase
    .from('invitations')
    .select('*, poa_order')
    .eq('estate_id', estateId)
    .order('created_at', { ascending: false });
  return { data, error };
};

// emailContext: { inviterName: string }
export const resendInvitation = async (invitationId, emailContext) => {
  const { data, error } = await supabase
    .from('invitations')
    .update({
      status: 'pending',
      expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    })
    .eq('id', invitationId)
    .select()
    .maybeSingle();

  if (!error && data && data.invited_email) {
    sendEmail(data.invited_email, 'invitation', {
      inviteeName: data.invited_name || '',
      inviterName: emailContext?.inviterName || '',
      roleName: data.role || 'Collaborator',
      inviteCode: data.invite_code || '',
      inviteeEmail: data.invited_email,
    });
  }

  return { data, error };
};

// ─── DEATH REPORT HELPERS ─────────────────────────────────────────────────────

// emailContext: { ownerEmail, ownerName, executorName }
export const reportDeath = async (userId, estateId, reportData, emailContext) => {
  const deadline = new Date(Date.now() + 72 * 60 * 60 * 1000);
  const { data, error } = await supabase
    .from('death_reports')
    .insert({
      estate_id: estateId,
      reported_by: userId,
      deceased_name: reportData.deceasedName,
      date_of_death: reportData.dateOfDeath,
      relationship: reportData.relationship,
    })
    .select()
    .maybeSingle();

  if (!error) {
    await supabase
      .from('estates')
      .update({ status: 'death_reported', death_reported_at: new Date().toISOString() })
      .eq('id', estateId);

    if (emailContext?.ownerEmail) {
      sendEmail(emailContext.ownerEmail, 'death_reported', {
        ownerName: emailContext.ownerName || '',
        executorName: emailContext.executorName || '',
        date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        deadline: deadline.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }),
        reportId: data?.id,
        userId: emailContext.ownerId,
      });
    }
  }
  return { data, error };
};

// emailContext: { executorEmail, ownerName }
export const cancelDeathReport = async (reportId, emailContext) => {
  const { data, error } = await supabase
    .from('death_reports')
    .update({ status: 'cancelled', cancelled_at: new Date().toISOString() })
    .eq('id', reportId)
    .select()
    .maybeSingle();

  if (!error && data?.estate_id) {
    await supabase
      .from('estates')
      .update({ status: 'planning', death_reported_at: null })
      .eq('id', data.estate_id);

    if (emailContext?.executorEmail) {
      sendEmail(emailContext.executorEmail, 'death_cancelled', {
        ownerName: emailContext.ownerName || '',
      });
    }
  }
  return { data, error };
};

// emailContext: { executorEmail, deceasedName }
export const confirmDeath = async (reportId, estateId, emailContext) => {
  const now = new Date().toISOString();
  await supabase
    .from('death_reports')
    .update({ status: 'confirmed', confirmed_at: now })
    .eq('id', reportId);

  const { data, error } = await supabase
    .from('estates')
    .update({ status: 'active_settlement', death_confirmed_at: now })
    .eq('id', estateId)
    .select()
    .maybeSingle();

  if (!error && emailContext?.executorEmail) {
    sendEmail(emailContext.executorEmail, 'death_confirmed', {
      deceasedName: emailContext.deceasedName || '',
    });
  }

  // Cross-estate notification: mark this person deceased in other estates' succession chains
  if (!error && data?.owner_email) {
    sendEmail('', 'notify_named_persons_deceased', {
      deceasedEmail: data.owner_email,
      deceasedName: emailContext?.deceasedName || data.deceased_name || '',
    });
  }

  return { data, error };
};

export const getActiveDeathReport = async (estateId) => {
  const { data, error } = await supabase
    .from('death_reports')
    .select('*')
    .eq('estate_id', estateId)
    .in('status', ['pending', 'confirmed'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return { data, error };
};

export const getDeathReportById = async (reportId) => {
  const { data, error } = await supabase
    .from('death_reports')
    .select('*, estates(*)')
    .eq('id', reportId)
    .maybeSingle();
  return { data, error };
};

// ─── NOTIFICATION HELPERS ─────────────────────────────────────────────────────

export const logNotification = async (userId, type, subject, body, email) => {
  const { data, error } = await supabase
    .from('notifications')
    .insert({ user_id: userId, type, subject, body, sent_to_email: email, status: 'pending' })
    .select()
    .maybeSingle();
  return { data, error };
};

export const getNotifications = async (userId) => {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(20);
  return { data, error };
};

export const markNotificationRead = async (notificationId) => {
  const { data, error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('id', notificationId)
    .select()
    .maybeSingle();
  return { data, error };
};

export const markAllNotificationsRead = async (userId) => {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('read', false);
  return { error };
};

// ─── ESTATE ID HELPER ─────────────────────────────────────────────────────────

export const loadEstateById = async (estateId) => {
  const { data, error } = await supabase
    .from('estates')
    .select('*')
    .eq('id', estateId)
    .maybeSingle();
  return { data, error };
};

export const getEstateId = async (userId) => {
  const { data, error } = await supabase
    .from('estates')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle();
  return { data, error };
};

// ─── PERMISSIONS & ACCESS REQUESTS ───────────────────────────────────────────

export const updateInvitationPermissions = async (invitationId, permissions) => {
  const { data, error } = await supabase
    .from('invitations')
    .update({ permissions })
    .eq('id', invitationId)
    .select()
    .maybeSingle();
  return { data, error };
};

export const createAccessRequest = async (requestData) => {
  const { data, error } = await supabase
    .from('access_requests')
    .insert(requestData)
    .select()
    .maybeSingle();
  return { data, error };
};

export const getAccessRequestById = async (requestId) => {
  const { data, error } = await supabase
    .from('access_requests')
    .select('*')
    .eq('id', requestId)
    .maybeSingle();
  return { data, error };
};
export const getActiveAccessRequest = async (estateId) => {
  const { data, error } = await supabase
    .from('access_requests')
    .select('*')
    .eq('estate_id', estateId)
    .in('status', ['pending_backup1', 'pending_primary', 'pending_owner', 'granted'])
    .order('created_at', { ascending: false })
    .maybeSingle();
  return { data, error };
};

export const respondToAccessRequest = async (requestId, response, reason) => {
  const { data, error } = await supabase
    .from('access_requests')
    .update({
      status: response === 'denied' ? 'denied' : 'granted',
      denied_by: response === 'denied' ? 'approver' : null,
      denied_at: response === 'denied' ? new Date().toISOString() : null,
      denial_reason: reason || null,
      granted_at: response === 'granted' ? new Date().toISOString() : null,
    })
    .eq('id', requestId)
    .select()
    .maybeSingle();
  return { data, error };
};

export const updateOwnerAccessSettings = async (userId, settings) => {
  const { error } = await supabase
    .from('profiles')
    .update({
      executor_response_window: settings.responseWindow,
      grant_immediate_access: settings.grantImmediateAccess,
    })
    .eq('id', userId);
  return { error };
};

export const getInvitationForUser = async (email, estateId) => {
  const { data, error } = await supabase
    .from('invitations')
    .select('id, permissions, role, status, invited_email, inviter_name')
    .ilike('invited_email', email)
    .eq('estate_id', estateId)
    .maybeSingle();
  return { data, error };
};

// Returns ALL accepted invitation rows for this user+estate (not just one).
// Use this wherever permissions need to be merged across multiple roles.
// NOTE: only returns status='accepted' rows — pending_acknowledgment rows have zero
// effect on permissions until the collaborator explicitly acknowledges them.
export const getInvitationsForUser = async (email, estateId) => {
  const { data, error } = await supabase
    .from('invitations')
    .select('id, permissions, role, status, invited_email, inviter_name, poa_order')
    .ilike('invited_email', email)
    .eq('estate_id', estateId)
    .eq('status', 'accepted');
  return { data: data || [], error };
};

// ─── ADD ROLE (pending_acknowledgment) HELPERS ────────────────────────────────

// Insert one invitation row per selected role with status='pending_acknowledgment'.
// No invite_code is generated — the collaborator is already on the platform.
export const createAddRoleInvitations = async (estateId, invitedById, rows) => {
  // rows: Array<{ email, name, role, executor_order?, medical_poa_order?, poa_order?, permissions? }>
  if (!estateId || !invitedById || !rows?.length) return { data: null, error: new Error('Missing required fields') };

  const payload = rows.map(r => ({
    estate_id: estateId,
    invited_by: invitedById,
    invited_email: r.email.toLowerCase().trim(),
    invited_name: r.name,
    role: r.role,
    permissions: r.permissions || {},
    status: 'pending_acknowledgment',
    invite_code: null,
    ...(r.executor_order != null ? { executor_order: r.executor_order } : {}),
    ...(r.medical_poa_order != null ? { medical_poa_order: r.medical_poa_order } : {}),
    ...(r.poa_order != null ? { poa_order: r.poa_order } : {}),
    ...(r.inviterName ? { inviter_name: r.inviterName } : {}),
  }));

  const { data, error } = await supabase
    .from('invitations')
    .insert(payload)
    .select();
  return { data, error };
};

// Collaborator acknowledges a pending_acknowledgment row → set status='accepted'
export const acknowledgeRole = async (invitationId, userId) => {
  const { data, error } = await supabase
    .from('invitations')
    .update({ status: 'accepted', accepted_by: userId, accepted_at: new Date().toISOString() })
    .eq('id', invitationId)
    .select()
    .maybeSingle();
  return { data, error };
};

// Collaborator declines a pending_acknowledgment row → set status='declined'
export const declineRole = async (invitationId) => {
  const { data, error } = await supabase
    .from('invitations')
    .update({ status: 'declined' })
    .eq('id', invitationId)
    .select()
    .maybeSingle();
  return { data, error };
};

// Returns all pending_acknowledgment invitations for the given email across all estates.
export const getPendingAcknowledgments = async (email) => {
  const { data, error } = await supabase
    .from('invitations')
    .select('*, estates(owner_name, owner_email, user_id)')
    .ilike('invited_email', email)
    .eq('status', 'pending_acknowledgment')
    .order('created_at', { ascending: false });
  return { data: data || [], error };
};

// ─── COUPLE LINKING ───────────────────────────────────────────────────────────

// Look up a couple invite by code alone (no email required — used on landing page).
// Same rationale as findInvitationByCode above -- pre-auth lookup moved to a
// SECURITY DEFINER function so the permissive invitations SELECT policy can
// be dropped. Returns the same fields, minus the invite token.
export const findCoupleInvitationByCode = async (code) => {
  const { data, error } = await supabase.rpc('lookup_couple_invitation_by_code', {
    p_code: code.toUpperCase().trim(),
  });
  return { data: data ?? null, error };
};

// Set linked_spouse_estate_id on both estates reciprocally.
export const setLinkedSpouseEstates = async (estateId1, estateId2) => {
  const [r1, r2] = await Promise.all([
    supabase.from('estates').update({ linked_spouse_estate_id: estateId2 }).eq('id', estateId1),
    supabase.from('estates').update({ linked_spouse_estate_id: estateId1 }).eq('id', estateId2),
  ]);
  return { error: r1.error || r2.error || null };
};

// Mark spouse setup as seen/completed on the profile.
export const markSpouseSetupDone = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ spouse_setup_done: true })
    .eq('id', userId)
    .select()
    .maybeSingle();
  return { data, error };
};

// Wire up reciprocal succession roles + estate link via the Edge Function
// (uses service role so it can insert on both estates regardless of ownership).
export const setupCoupleLink = async ({
  inviterEstateId, inviterUserId, inviterName, inviterEmail,
  inviteeEstateId, inviteeUserId, inviteeName, inviteeEmail,
  coupleRoles,
}) => {
  const { data, error } = await supabase.functions.invoke('send-email', {
    body: {
      type: 'setup_couple_link',
      inviterEstateId, inviterUserId, inviterName, inviterEmail,
      inviteeEstateId, inviteeUserId, inviteeName, inviteeEmail,
      coupleRoles,
    },
  });
  return { data, error };
};

// ─── COUPLE SIGNUP ($399 bundle) ─────────────────────────────────────────────

// Create a pending couple-signup invitation record before redirecting to Stripe.
// estate_id is intentionally null — neither spouse has an estate yet.
export const createCoupleSignupRecord = async (userId, {
  spouse1Name, spouse1Email,
  spouse2Name, spouse2Email,
  selectedRoles,
}) => {
  const invite_code = generateInviteCode();
  const { data, error } = await supabase
    .from('invitations')
    .insert({
      invited_by:          userId,
      estate_id:           null,
      invited_email:       spouse2Email.toLowerCase().trim(),
      invited_name:        spouse2Name.trim(),
      inviter_name:        spouse1Name.trim(),
      first_spouse_email:  spouse1Email.toLowerCase().trim(),
      invite_code,
      role:                'Couple',
      permissions:         {},
      status:              'pending',
      is_couple_invite:    true,
      is_couple_signup:    true,
      couple_roles:        selectedRoles,
    })
    .select()
    .maybeSingle();
  return { data, error, invite_code };
};

// Called during spouse 2's handleOnboardingComplete (and the sign-in/create-account
// paths as a safety net). Claims the pre-created placeholder estate the webhook
// created for this user by setting user_id, marking the profile paid, and setting
// spouse_setup_done so the SpouseSetupScreen gate never appears.
//
// Evidence-based guard: before touching any estate, we require a corroborating
// invitations row with is_couple_signup = true and invited_email = ownerEmail.
// This prevents accidentally claiming an unrelated estate that happens to share
// the same email address (which would overwrite the real owner's own_estate_id).
// Non-couple-signup users calling this unconditionally get { data: null, error: null }
// immediately — it is a true no-op for them.
//
// Two-path estate lookup (both tried; first match wins):
//   Path 1 — user_id IS NULL AND owner_email = ownerEmail
//   Path 2 — user_id IS NULL AND linked_spouse_estate_id = spouseEstateId
//             (fallback for cases where the webhook wrote the wrong email on the
//              placeholder — observed in couple-signup tests)
export const claimPreCreatedEstate = async (userId, ownerEmail, spouseEstateId, _client = supabase) => {
  const normalizedEmail = ownerEmail.toLowerCase().trim();
  console.log('[CLAIM] called — userId:', userId, '| ownerEmail:', normalizedEmail, '| spouseEstateId:', spouseEstateId);

  // ── Evidence check: require a corroborating couple-signup invite ─────────
  console.log('[CLAIM] querying invitations for corroboration — is_couple_signup=true, invited_email ilike', normalizedEmail);
  const { data: coupleInvite, error: inviteErr } = await _client
    .from('invitations')
    .select('id, estate_id, couple_roles')
    .eq('is_couple_signup', true)
    .ilike('invited_email', normalizedEmail)
    .maybeSingle();
  console.log('[CLAIM] corroboration result — data:', JSON.stringify(coupleInvite), '| error:', JSON.stringify(inviteErr));

  if (!coupleInvite) {
    console.log('[CLAIM] no corroborating invite found — returning no-op');
    return { data: null, error: null };
  }
  console.log('[CLAIM] corroborated by invite id:', coupleInvite.id);

  // ── Path 1: find unclaimed estate by email ───────────────────────────────
  console.log('[CLAIM] Path 1 — querying estates WHERE user_id IS NULL AND owner_email =', normalizedEmail);
  const { data: estateByEmail, error: emailErr } = await _client
    .from('estates')
    .select('id, linked_spouse_estate_id')
    .is('user_id', null)
    .eq('owner_email', normalizedEmail)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  console.log('[CLAIM] Path 1 result — data:', JSON.stringify(estateByEmail), '| error:', JSON.stringify(emailErr));

  // ── Path 2: fallback via linked_spouse_estate_id ─────────────────────────
  let estateByLink = null;
  if (!estateByEmail && spouseEstateId) {
    console.log('[CLAIM] Path 2 — querying estates WHERE user_id IS NULL AND linked_spouse_estate_id =', spouseEstateId);
    const { data: linkData, error: linkErr } = await _client
      .from('estates')
      .select('id, linked_spouse_estate_id')
      .is('user_id', null)
      .eq('linked_spouse_estate_id', spouseEstateId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    console.log('[CLAIM] Path 2 result — data:', JSON.stringify(linkData), '| error:', JSON.stringify(linkErr));
    estateByLink = linkData ?? null;
  } else if (!spouseEstateId) {
    console.log('[CLAIM] Path 2 skipped — no spouseEstateId provided');
  } else {
    console.log('[CLAIM] Path 2 skipped — Path 1 already found an estate');
  }

  const estate = estateByEmail ?? estateByLink;
  if (!estate) {
    console.log('[CLAIM] no unclaimed estate found — returning error');
    return { data: null, error: emailErr || new Error('No pre-created estate found for: ' + normalizedEmail) };
  }
  console.log('[CLAIM] estate to claim:', estate.id, '| found via:', estateByEmail ? 'Path 1 (email)' : 'Path 2 (linked_spouse_estate_id)');

  // ── Claim: write user_id so RLS + all subsequent queries work ────────────
  console.log('[CLAIM] >>> UPDATE estates SET user_id =', userId, ', owner_user_id =', userId, 'WHERE id =', estate.id);
  const { data, error } = await _client
    .from('estates')
    .update({ user_id: userId, owner_user_id: userId })
    .eq('id', estate.id)
    .select()
    .maybeSingle();
  console.log('[CLAIM] <<< estates UPDATE result — data:', JSON.stringify(data), '| error:', JSON.stringify(error));

  if (!error) {
    console.log('[CLAIM] >>> RPC sync_own_estate_after_claim — sets own_estate_id + spouse_setup_done server-side; has_paid is NOT set here (granted later by claim_couple_entitlement)');
    const { data: syncData, error: profErr } = await _client.rpc('sync_own_estate_after_claim');
    console.log('[CLAIM] <<< sync_own_estate_after_claim result — data:', JSON.stringify(syncData), '| error:', JSON.stringify(profErr));
    if (profErr || !syncData?.ok) {
      console.error('[CLAIM] sync_own_estate_after_claim FAILED — error:', JSON.stringify(profErr), '| reason:', syncData?.reason ?? 'no reason returned');
    }

    console.log('[CLAIM] >>> UPDATE invitations SET accepted_by =', userId, 'WHERE estate_id =', estate.id, 'AND accepted_by IS NULL');
    const { data: invData, error: invUpdErr, count } = await _client.from('invitations')
      .update({ accepted_by: userId })
      .eq('estate_id', estate.id)
      .is('accepted_by', null)
      .select();
    console.log('[CLAIM] <<< invitations UPDATE result — data:', JSON.stringify(invData), '| count:', count, '| error:', JSON.stringify(invUpdErr));
  } else {
    console.log('[CLAIM] estates UPDATE failed — skipping profile and invitation writes');
  }

  return { data, error };
};

// ─── COUPLE ESTATE + ROLE LINKER ──────────────────────────────────────────────
// Called from exactly ONE place: immediately after claimPreCreatedEstate succeeds
// inside handleOnboardingComplete. At this point both spouses have real user IDs,
// so every invitation row can be created with correct accepted_by on both sides.
//
// What this does in a single pass:
//   1. For each role in coupleRoles: inserts TWO rows (bidirectional):
//        Direction A — sp1 holds the role on sp2's estate (accepted_by = sp1UserId)
//        Direction B — sp2 holds the role on sp1's estate (accepted_by = sp2UserId)
//   2. Idempotently sets linked_spouse_estate_id on sp2's estate.
//      (The webhook set it too; this is belt-and-suspenders.)
//
// Permissions come from ROLE_PERMISSIONS (defined above) — the same source
// used by supabase/functions/_shared/rolePermissions.ts.
export const linkCoupleEstatesAndRoles = async ({
  sp2UserId, sp2EstateId, sp2Email, sp2Name,
  sp1UserId, sp1EstateId, sp1Email, sp1Name,
  coupleRoles,
  _client = supabase,
}) => {
  if (!Array.isArray(coupleRoles) || coupleRoles.length === 0) {
    console.log('[HeirOS] linkCoupleEstatesAndRoles: no roles selected — skipping invitation rows');
  } else {
    const now = new Date().toISOString();
    const roleOrderMap = {
      [ROLE_EXECUTOR]:    'executor_order',
      [ROLE_MEDICAL_POA]: 'medical_poa_order',
      [ROLE_POA]:         'poa_order',
    };
    const rows = [];
    for (const role of coupleRoles) {
      const orderField = roleOrderMap[role];
      if (!orderField) {
        console.warn('[HeirOS] linkCoupleEstatesAndRoles: unknown role skipped:', role);
        continue;
      }
      const perms = ROLE_PERMISSIONS[role] ?? {};
      // Direction A: sp1 holds the role on sp2's estate
      rows.push({
        estate_id:     sp2EstateId,
        invited_by:    sp2UserId,
        invited_email: sp1Email.toLowerCase(),
        invited_name:  sp1Name,
        inviter_name:  sp2Name,
        role,
        permissions:   perms,
        status:        'accepted',
        accepted_by:   sp1UserId || null,
        accepted_at:   now,
        invite_code:   null,
        [orderField]:  1,
      });
      // Direction B: sp2 holds the role on sp1's estate
      if (sp1EstateId) {
        rows.push({
          estate_id:     sp1EstateId,
          invited_by:    sp1UserId || sp2UserId,
          invited_email: sp2Email.toLowerCase(),
          invited_name:  sp2Name,
          inviter_name:  sp1Name,
          role,
          permissions:   perms,
          status:        'accepted',
          accepted_by:   sp2UserId,
          accepted_at:   now,
          invite_code:   null,
          [orderField]:  1,
        });
      }
    }
    console.log('[HeirOS] linkCoupleEstatesAndRoles: inserting', rows.length, 'rows:',
      rows.map(r => `${r.role} on ${r.estate_id}`).join(', '));
    const { error: invErr } = await _client.from('invitations').insert(rows);
    if (invErr) {
      console.error('[HeirOS] linkCoupleEstatesAndRoles: invitation insert failed:', invErr.message);
    } else {
      console.log('[HeirOS] linkCoupleEstatesAndRoles: all role rows inserted successfully');
    }
  }

  // Idempotently confirm linked_spouse_estate_id on sp2's estate
  if (sp1EstateId && sp2EstateId) {
    const { error: linkErr } = await _client
      .from('estates')
      .update({ linked_spouse_estate_id: sp1EstateId })
      .eq('id', sp2EstateId);
    if (linkErr) {
      console.warn('[HeirOS] linkCoupleEstatesAndRoles: sp2 estate link update failed:', linkErr.message);
    } else {
      console.log('[HeirOS] linkCoupleEstatesAndRoles: confirmed sp2 estate', sp2EstateId, '↔ sp1 estate', sp1EstateId);
    }
  }
};
